import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { entitiesEndpoints } from "./entities.endpoints";
import type {
  CreateEntityPayload,
  CreateEntityResponse,
  EntityVisitRecord,
  EntityVisitResponse,
  IEntityDetailsResponse,
  SendOtpPayload,
  SendOtpResponse,
  TEntityOverviewResponse,
  UpdateEntityBrandingPayload,
  UpdateEntityTemplatesPayload,
  UpdateEntityPocPayload,
  UpdateEntitySSOPayload,
} from "./entities.mutations.types";

export const createEntity = async (payload: CreateEntityPayload): Promise<CreateEntityResponse> => {
  const { data } = await axiosInstance.post(entitiesEndpoints.entities, payload);
  return data;
};

export const sendOtp = async (payload: SendOtpPayload): Promise<SendOtpResponse> => {
  const { data } = await axiosInstance.post(entitiesEndpoints.sendOtp, payload);
  return data;
};

export const editEntity = async ({
  payload,
  entityId,
}: {
  payload: TEntityOverviewResponse | UpdateEntityBrandingPayload | UpdateEntityTemplatesPayload;
  entityId: string;
}): Promise<IEntityDetailsResponse> => {
  const { data } = await axiosInstance.put(`${entitiesEndpoints.details}/${entityId}`, payload);
  return data;
};

export const updateEntityPoc = async ({
  entityId,
  payload,
}: {
  entityId: string;
  payload: UpdateEntityPocPayload;
}): Promise<IEntityDetailsResponse> => {
  const { data } = await axiosInstance.put(`${entitiesEndpoints.details}/${entityId}`, payload);
  return data;
};

export const updateEntitySSO = async ({
  entityId,
  payload,
}: {
  entityId: string;
  payload: UpdateEntitySSOPayload;
}): Promise<IEntityDetailsResponse> => {
  const { data } = await axiosInstance.put(`${entitiesEndpoints.details}/${entityId}`, payload, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data;
};

export const deactivateEntity = async (entityId: string): Promise<void> => {
  const { data } = await axiosInstance.patch(
    `${entitiesEndpoints.deactivate}/${entityId}/deactivate`,
  );
  return data;
};

export const activateEntity = async (entityId: string): Promise<void> => {
  const { data } = await axiosInstance.patch(
    `${entitiesEndpoints.deactivate}/${entityId}/activate`,
  );
  return data;
};

// Fire-and-forget behind a picker switch (SERV-2793): the switch has already
// happened, so a failed record must not toast. Silence lives on the request
// config because the axios interceptor, not the mutation callback, is what
// raises the alert.
export const recordEntityVisit = async (entityId: string): Promise<EntityVisitRecord> => {
  const { data } = await axiosInstance.post<EntityVisitResponse>(
    `${entitiesEndpoints.entityVisit}/${entityId}/visit`,
    undefined,
    { suppressErrorAlert: true } as CustomAxiosRequestConfig,
  );
  return data.data;
};
